import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Smart scroll management for route changes:
 * - Forward navigation (clicking links): scrolls to top of new page
 * - Back/forward browser buttons: restores previous scroll position
 *
 * Scroll positions are kept in a ref for the current SPA session (in-memory).
 */
export const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const scrollPositions = useRef<Record<string, number>>({});

  useEffect(() => {
    // Determine the scrollable element (company-page-scroll pages use
    // document.documentElement; the app layout may use #root or window)
    const getScrollY = () =>
      window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

    const scrollTo = (y: number) => {
      window.scrollTo({ top: y, left: 0 });
      document.documentElement.scrollTop = y;
      document.body.scrollTop = y;
    };

    // Try to restore from sessionStorage for POP (back/forward)
    if (navigationType === 'POP') {
      const saved = scrollPositions.current[pathname];
      if (saved !== undefined) {
        // Small delay lets the DOM render before restoring position
        requestAnimationFrame(() => {
          scrollTo(saved);
        });
      }
    } else {
      // PUSH or REPLACE — new navigation, scroll to top
      scrollTo(0);
    }

    // Save current scroll position when leaving this page
    return () => {
      scrollPositions.current[pathname] = getScrollY();
    };
  }, [pathname, navigationType]);

  return null;
};

export default ScrollToTop;
